/**
 * The record as a designer source: every verb bound to one recipe id.
 *
 * `Designer` calls these and composes nothing. The type page has its own source with the
 * same verbs on the type-library endpoints — one component, two places the truth lives.
 */
import * as api from "./designerApi";
import { sopApi } from "@/utils/sopApi";

export function recordSource(recipeId) {
  return {
    kind: "record",
    id: recipeId,
    backTo: "/record-designer",
    backLabel: "Back to records",
    showRole: true,
    // The MFR library page; the record's MFR facts are in the envelope already.
    openMfr: (navigate) => navigate("/mfr"),
    newKey: api.newKey,
    getVocabulary: api.getVocabulary,
    listChecklists: api.listChecklists,
    listClearanceProfiles: api.listClearanceProfiles,
    listRules: api.listRules,
    listRoles: api.listRoles,
    getProposedOrder: api.getProposedOrder,
    placeRows: api.placeRows,
    load: () => api.getRecipe(recipeId),
    // RV9p · who has the record open for editing.
    lock: {
      get: () => api.getLock(recipeId),
      claim: () => api.claimLock(recipeId),
      heartbeat: () => api.heartbeatLock(recipeId),
      release: () => api.releaseLock(recipeId),
      takeOver: (reason) => api.takeOverLock(recipeId, reason),
    },
    insertStep: (body, ctx) => api.insertStep(recipeId, body, ctx),
    copyStep: (code, body, ctx) => api.copyStep(recipeId, code, body, ctx),
    insertDecision: (body, ctx) => api.insertDecision(recipeId, body, ctx),
    bomFromMfr: (body, ctx) => api.bomFromMfr(recipeId, body, ctx),
    editStep: (code, body, ctx) => api.editStep(recipeId, code, body, ctx),
    moveStep: (code, body, ctx) => api.moveStep(recipeId, code, body, ctx),
    deleteStep: (code, body, ctx) => api.deleteStep(recipeId, code, body, ctx),
    getImpact: (code) => api.getImpact(recipeId, code),
    bindRule: (code, body, ctx) => api.bindRule(recipeId, code, body, ctx),
    tightenRule: (code, id, limits, ctx) => api.tightenRule(recipeId, code, id, limits, ctx),
    unbindRule: (code, id, ctx) => api.unbindRule(recipeId, code, id, ctx),
    readAqlSetup: (code) => api.readAqlSetup(recipeId, code),
    saveAqlSetup: (code, setup, ctx) => api.saveAqlSetup(recipeId, code, setup, ctx),
    refreshRuleFromPolicy: (code, id, ctx) => api.refreshRuleFromPolicy(recipeId, code, id, ctx),
    refreshRulesFromPolicy: (ctx) => api.refreshRulesFromPolicy(recipeId, ctx),
    setConcurrency: (operationId, body, ctx) => api.setConcurrency(recipeId, operationId, body, ctx),
    setHandover: (operationId, body, ctx) => api.setHandover(recipeId, operationId, body, ctx),
    setLotStep: (operationId, body, ctx) => api.setLotStep(recipeId, operationId, body, ctx),
    setReconciliation: (operationId, body, ctx) => api.setReconciliation(recipeId, operationId, body, ctx),
    setDependsOn: (operationId, body, ctx) => api.setDependsOn(recipeId, operationId, body, ctx),
    // An operation made straight from its type (Add Operation → Custom), and its own facts.
    addOperation: (body, ctx) => api.addOperation(recipeId, body, ctx),
    editOperation: (operationId, body, ctx) => api.editOperation(recipeId, operationId, body, ctx),
    listDepartments: api.listDepartments,
    wrapInLoop: (operationId, body, ctx) => api.wrapInLoop(operationId, body, ctx),
    setArea: (operationId, body, ctx) => api.setArea(recipeId, operationId, body, ctx),
    setDispensing: (operationId, body, ctx) => api.setDispensing(recipeId, operationId, body, ctx),
    listAreas: () => api.listAreas(),
    promote: (operationId, body, ctx) => api.promoteOperation(recipeId, operationId, body, ctx),
    submitForReview: () => api.submitForReview(recipeId),
    listParameters: api.listParameters,
    listControlCategories: api.listControlCategories,
    createChecklistItem: api.createChecklistItem,
    setChecklistItems: api.setChecklistItems,
    createChecklist: api.createChecklist,
    createParameter: api.createParameter,
    listLabels: api.listLabels,
    createLabel: api.createLabel,
    listUnits: api.listUnits,
    addField: (code, body, ctx) => api.addField(recipeId, code, body, ctx),
    editField: (code, fieldId, body, ctx) => api.editField(recipeId, code, fieldId, body, ctx),
    deleteField: (code, fieldId, ctx) => api.deleteField(recipeId, code, fieldId, ctx),
    typeUpdatePreview: (operationId) => api.typeUpdatePreview(recipeId, operationId),
    updateFromType: (operationId, body, ctx) => api.updateFromType(recipeId, operationId, body, ctx),
    uploadAttachment: (code, file, meta, ctx) => api.uploadAttachment(recipeId, code, file, meta, ctx),
    deleteAttachment: (code, id, ctx) => api.deleteAttachment(recipeId, code, id, ctx),
    linkSop: (code, body, ctx) => api.linkSop(recipeId, code, body, ctx),
    unlinkSop: (code, linkId, body, ctx) => api.unlinkSop(recipeId, code, linkId, body, ctx),
    // Effective procedures in the SOP library, by number or title.
    searchSops: async (q) => {
      const res = await sopApi.list({ q, status: "EFFECTIVE" });
      const rows = res?.data?.rows || res?.data || res?.rows || [];
      return Array.isArray(rows) ? rows : [];
    },
  };
}
